import type { ReactNode } from 'react';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../Button/Button';
import { Typography } from '../Typography';
import styles from './Slider.module.scss';

interface DotIndiactorSliderProps {
	array: ReactNode[];
	endClick?: () => void;
	onChange?: (currentIndex: number) => void;
}

const SWIPE_THRESHOLD = 10;

const DotIndicatorSlider = ({ array, endClick, onChange }: DotIndiactorSliderProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'splashScreen' });
	const [currentIndex, setCurrentIndex] = useState(0);
	const [dragOffset, setDragOffset] = useState(0);
	const [isDragging, setIsDragging] = useState(false);
	const touchStartX = useRef<number | null>(null);
	const screenWidthReference = useRef(window.innerWidth);

	const isFirstSlide = currentIndex === 0;
	const isLastSlide = currentIndex === array.length - 1;

	const handleNext = () => {
		if (isLastSlide) {
			endClick?.() ?? setCurrentIndex(0);
		} else {
			setCurrentIndex((previous) => previous + 1);
			onChange?.(currentIndex + 1);
		}
	};

	const handleTouchStart = (e: React.TouchEvent) => {
		touchStartX.current = e.touches[0].clientX;
		setIsDragging(true);
	};

	const handleTouchMove = (e: React.TouchEvent) => {
		if (touchStartX.current !== null) {
			const deltaX = e.touches[0].clientX - touchStartX.current;

			if ((isFirstSlide && deltaX > 0) || (isLastSlide && deltaX < 0)) {
				return;
			}

			setDragOffset(deltaX);
		}
	};

	const handleTouchEnd = () => {
		setIsDragging(false);

		if (dragOffset < -SWIPE_THRESHOLD && !isLastSlide) {
			setCurrentIndex((previous) => previous + 1);
			onChange?.(currentIndex + 1);
		} else if (dragOffset > SWIPE_THRESHOLD && !isFirstSlide) {
			setCurrentIndex((previous) => previous - 1);
			onChange?.(currentIndex - 1);
		}

		setDragOffset(0);
	};

	const getTransformStyle = () => {
		const base = -currentIndex * 100;
		const dragPercent = (dragOffset / screenWidthReference.current) * 100;

		return `translateX(calc(${base}% + ${dragPercent}%))`;
	};

	const renderDots = () => (
		<div className={styles.dotWrapper}>
			{array.length > 1 ? (
				array.map((_, index) => (
					<div key={`Dot ${index + 1}`} className={styles[index === currentIndex ? 'currentDot' : 'defaultDot']} />
				))
			) : (
				<div key='Dot 1' className={styles.currentDot} style={{ opacity: 0 }} />
			)}
		</div>
	);

	return (
		<div className={styles.dotSliderContainer}>
			<div
				className={styles.dotSlideContent}
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				onTouchEnd={handleTouchEnd}
			>
				<div
					className={styles.dotSlidesWrapper}
					style={{
						transform: getTransformStyle(),
						transition: isDragging ? 'none' : 'transform 0.6s ease',
					}}
				>
					{array.map((item, index) => (
						<div key={`DotSlide ${index + 1}`} className={styles.dotSlide}>
							{item}
						</div>
					))}
				</div>
			</div>

			<div className={styles.sliderFooter}>
				<div className={styles.buttonWrapper}>
					<Button onClick={handleNext}>
						<Typography variant='title1' variantS='headline1' textTransform='uppercase'>
							{isLastSlide ? t('startGame') : t('next')}
						</Typography>
					</Button>
				</div>

				<div className={styles.bottomControls}>{renderDots()}</div>
			</div>
		</div>
	);
};

export default DotIndicatorSlider;
